import { isInPolygon } from '@wix/wixrest-utils/dist/GeoUtils';
import {
  DispatchType,
  DispatchInfo,
  Availability,
  AvailabilityIteratorFactory,
  IntegerInterval,
  getDateOptions,
  getAsapParams,
  getDaysDiff,
  getTimeOptions,
  Restaurant,
  getFinalAvailability,
  getFinalAvailabilityIteratorFactory,
  VirtualDispatchType,
  getAvailableDeliveryInfos,
  DeliveryDispatchInfo,
  Address,
  isDeliveryPartnerArea,
  getNumberOfDaysAheadAvailableForOrders,
} from '@wix/restaurants-client-logic';
import { DropdownOptionProps } from 'wix-ui-tpa/dist/src/components/Dropdown';
import moment from 'moment-timezone';
import { TimingOption } from '../DispatchSettings/DispatchSettings';
import _ from 'lodash';
import { getDispatchTypeFromVirtual } from '../../../../core/logic/dispatchLogic';
import { PartialLocation } from '../../../../core/oloApi';
import { TranslationFunction } from 'yoshi-flow-editor-runtime';
import { BusyBlock } from '@wix/ambassador-restaurants-order-pacing-pacer/types';

interface getAsapTextArgs {
  deliveryInfos: DispatchInfo[];
  timezone: string;
  dispatchType: VirtualDispatchType;
  t: TranslationFunction;
  idealDeliveryArea?: DispatchInfo;
  locale: string;
  orderPacingLevel?: number;
}

export const getParamsForIntlFormat = (valueInMins?: number): { value: number; timeUnit: TimeUnit } => {
  if (!valueInMins) {
    return { value: 0, timeUnit: 'minute' };
  }
  const HOUR = 60;
  const DAY = 60 * 24;
  return valueInMins % DAY === 0
    ? {
        value: valueInMins / DAY,
        timeUnit: 'day',
      }
    : valueInMins % HOUR === 0
    ? { value: valueInMins / 60, timeUnit: 'hour' }
    : {
        value: valueInMins,
        timeUnit: 'minute',
      };
};

type TimeUnit = 'minute' | 'day' | 'hour';

export const humanizeMinutesDuration = (
  locale = 'en_US',
  timeFormatParams: { value: number; timeUnit: TimeUnit | '' },
) => {
  if (timeFormatParams === undefined || timeFormatParams.timeUnit === '') {
    return '';
  }
  try {
    // @ts-ignore
    const rtf = new Intl.RelativeTimeFormat(locale.split('_')[0], { numeric: 'auto' });
    return rtf.format(timeFormatParams.value, timeFormatParams.timeUnit);
  } catch (e) {
    return `in ${timeFormatParams.value} ${timeFormatParams.timeUnit}${timeFormatParams.value > 1 ? 's' : ''}`;
  }
};

const getHuminzedTimeUnit = (t: TranslationFunction, timeFormatParams: { value: number; timeUnit: string }) => {
  return timeFormatParams.value > 1
    ? t(`checkout_main_deliverymethod_time_${timeFormatParams.timeUnit}s_label`)
    : t(`checkout_main_deliverymethod_time_${timeFormatParams.timeUnit}_label`);
};

export function getAsapText({
  idealDeliveryArea,
  deliveryInfos,
  dispatchType,
  t,
  locale,
  timezone,
  orderPacingLevel,
}: getAsapTextArgs): string {
  const asapParams = getAsapParams({
    idealDeliveryArea,
    deliveryInfos,
    dispatchType: getDispatchTypeFromVirtual(dispatchType),
    timezone,
    shouldConsiderDeliveryPartner: true,
    orderPacingLevel,
  });
  let text: string | undefined, min: number | undefined, max: number | undefined, minTimeParams;
  switch (asapParams.type) {
    case 'unavailable':
    case 'invalid':
    case 'immediate':
      text = '';
      break;
    case 'range':
      ({ min, max } = asapParams);
      minTimeParams = getParamsForIntlFormat(min);
      const maxTimeParams = getParamsForIntlFormat(max);
      const minString =
        minTimeParams.timeUnit !== maxTimeParams.timeUnit
          ? `${minTimeParams.value} ${getHuminzedTimeUnit(t, minTimeParams)}`
          : minTimeParams.value;

      const maxString = `${maxTimeParams.value} ${getHuminzedTimeUnit(t, maxTimeParams)}`;
      text = t('main_page_minmaxminutes', { min: minString, max: maxString });
      break;
    case 'min':
      ({ min } = asapParams);
      minTimeParams = getParamsForIntlFormat(min);
      text = t('main_page_order_pacing_minmaxminutes', {
        min: `${minTimeParams.value} ${getHuminzedTimeUnit(t, minTimeParams)}`,
      });
      break;
    case 'approx':
      ({ min } = asapParams);
      minTimeParams = getParamsForIntlFormat(min);
      text = t('main_page_order_pacing_approxminutes', {
        min: `${minTimeParams.value} ${getHuminzedTimeUnit(t, minTimeParams)}`,
      });
      break;
    case 'exact':
      text =
        dispatchType === 'delivery'
          ? t('main_page_maxminutes', {
              delayMin: `${getParamsForIntlFormat(asapParams.value).value} ${getHuminzedTimeUnit(
                t,
                getParamsForIntlFormat(asapParams.value),
              )}`,
            })
          : `(${humanizeMinutesDuration(locale, getParamsForIntlFormat(asapParams.value))})`;

      break;
    default:
      break;
  }
  return `${t('main_page_asap')} ${text}`;
}

export const formatDate = (timestamp: number, timezone: string, fallback: string, t: TranslationFunction): string => {
  const diff = getDaysDiff(timestamp, timezone);

  if (diff === 0) {
    return t('order_settings_modal_dropdown_today');
  } else if (diff === 1) {
    return t('order_settings_modal_dropdown_tomorrow');
  } else {
    return fallback;
  }
};

export const getDateDropdownOptions = (
  availability: Availability,
  timezone: string,
  numberOfDays: number,
  delayMins: IntegerInterval,
  t: TranslationFunction,
  locale: string,
  dishPrepareTime: number,
  availabilityIteratorFactory?: AvailabilityIteratorFactory,
): DropdownOptionProps[] => {
  if (locale === undefined) {
    return [];
  }
  // @ts-ignore
  const intl = new Intl.DateTimeFormat(locale.split('_')[0], { day: 'numeric', month: 'long', timeZone: timezone });
  return getDateOptions({
    availability,
    availabilityIteratorFactory,
    timezone,
    numberOfDays,
    delayMins,
    dishPrepareTime,
  }).map((o) => ({
    id: String(o.timestamp),
    value: formatDate(o.timestamp, timezone, intl.format(o.timestamp), t),
    isSelectable: true,
  }));
};

export const getTimeDropdownOptions = (
  availability: Availability,
  timezone: string,
  selectedDate: number,
  delayMins: IntegerInterval,
  dishPrepareTime: number,
  locale: string,
  availabilityIteratorFactory?: AvailabilityIteratorFactory,
): DropdownOptionProps[] => {
  return getTimeOptions({
    locale,
    availability,
    availabilityIteratorFactory,
    day: selectedDate,
    timezone,
    delayMins,
    dishPrepareTime,
  }).map((o) => ({
    id: String(o.timestamp),
    value: o.displayableValue,
    isSelectable: true,
  }));
};

export const getTimeOptionsWithNonSelectableBusyBlocks = (
  timeOptions: DropdownOptionProps[],
  busyBlocks?: BusyBlock[] | null,
) => {
  if (!busyBlocks || busyBlocks.length === 0) {
    return timeOptions;
  }
  const busyBlocksTimeStamps = busyBlocks.map((busyBlock) => busyBlock.startTime?.getTime());

  return timeOptions.map((timeOption) => ({
    ...timeOption,
    ...(busyBlocksTimeStamps.includes(Number(timeOption?.id)) ? { isSelectable: false } : {}),
  }));
};

function getMaxDeliveryDelayMins(restaurant: Restaurant | PartialLocation) {
  const availableDeliveryInfos = getAvailableDeliveryInfos(restaurant.deliveryInfos, restaurant.timezone);
  const delays = availableDeliveryInfos.filter((di) => di.type === 'delivery').map((di) => di.delayMins);

  return _.max(delays) || 0;
}

export function getDishPrepareTime(restaurant: Restaurant | PartialLocation, dispatchType: DispatchType) {
  const takeoutInfo = restaurant.deliveryInfos.find((di) => di.type === 'takeout');

  if (dispatchType === 'takeout' && takeoutInfo) {
    return takeoutInfo.delayMins || 0;
  } else if (dispatchType === 'delivery') {
    return getMaxDeliveryDelayMins(restaurant);
  } else {
    return 0;
  }
}

export interface GetTimeSelectorDetailsArgs {
  restaurant: Restaurant | PartialLocation;
  dispatchType: VirtualDispatchType;
  timingOption?: TimingOption;
  dispatchTime?: number;
  t: TranslationFunction;
  error?: string;
  idealDeliveryArea?: DispatchInfo;
  address?: Address;
}

export interface TimeSelectorDetails {
  dateError?: string;
  timeError?: string;
  finalAvailability: Availability;
  finalAvailabilityIteratorFactory?: AvailabilityIteratorFactory;
  dateOptions: DropdownOptionProps[];
  timeOptions: DropdownOptionProps[];
  selectedDate: number;
  dishPrepareTime: number;
}

export function getDispatchTimeSelectorDetails({
  timingOption = 'asap',
  dispatchTime,
  restaurant,
  dispatchType,
  t,
  error,
  idealDeliveryArea,
  address,
}: GetTimeSelectorDetailsArgs): TimeSelectorDetails {
  const { timezone, locale } = restaurant;
  const initialTime = moment(dispatchTime).tz(timezone);
  const numberOfDays = getNumberOfDaysAheadAvailableForOrders(restaurant.orders);
  const isDeliveryType = dispatchType === 'delivery';
  const matchingDeliveryAreas =
    isDeliveryType && address?.formatted ? getDeliveryAreasByAddress(restaurant, address) : undefined;

  const finalAvailabilityIteratorFactory = getFinalAvailabilityIteratorFactory(
    restaurant as Restaurant,
    getDispatchTypeFromVirtual(dispatchType),
    matchingDeliveryAreas,
  );
  const finalAvailability = getFinalAvailability(
    restaurant as Restaurant,
    getDispatchTypeFromVirtual(dispatchType),
    idealDeliveryArea,
    !isDeliveryType,
  );

  const dishPrepareTime = getDishPrepareTime(restaurant, getDispatchTypeFromVirtual(dispatchType));

  const dateOptions = getDateDropdownOptions(
    finalAvailability,
    timezone,
    numberOfDays,
    restaurant.orders.future.delayMins,
    t,
    restaurant.locale,
    dishPrepareTime,
    finalAvailabilityIteratorFactory,
  );
  const selectedDate = dispatchTime ? initialTime.startOf('day').valueOf() : Number(dateOptions[0]?.id);

  const timeOptions = getTimeDropdownOptions(
    finalAvailability,
    timezone,
    selectedDate,
    restaurant.orders.future.delayMins,
    dishPrepareTime,
    locale,
    finalAvailabilityIteratorFactory,
  );

  let dateError = timingOption !== 'asap' ? error : undefined;
  let timeError = timingOption !== 'asap' ? error : undefined;

  if (timingOption !== 'asap') {
    const selectedDateOption = dateOptions.find((option) => selectedDate === Number(option.id));
    if (selectedDateOption) {
      const selectedTimeOption = !dispatchTime || timeOptions.find((option) => option.id === String(dispatchTime));
      timeError = !selectedTimeOption && t('order_settings_modal_specifictime_errormessage');
    } else {
      dateError = t('order_settings_modal_specificdate_errormessage');
    }
  }

  return {
    dateError,
    timeError,
    dateOptions,
    timeOptions,
    finalAvailability,
    finalAvailabilityIteratorFactory,
    selectedDate,
    dishPrepareTime,
  };
}

export const getDeliveryAreasByAddress = (restaurant: Pick<Restaurant, 'deliveryInfos'>, address: Address) => {
  const deliveryAreas = restaurant.deliveryInfos.filter(
    (di) =>
      di.type === 'delivery' &&
      !di.inactive &&
      !isDeliveryPartnerArea(di) &&
      isInPolygon((di as DeliveryDispatchInfo).area.polygon, address.latLng),
  );
  const deliveryPartnerAreas = restaurant.deliveryInfos.filter((info) => isDeliveryPartnerArea(info) && !info.inactive);
  return deliveryAreas.concat(deliveryPartnerAreas);
};
